'use strict';
const { aggregateBudgetForecasts } = require('./budgetForecast');
const cents = value => Math.round(Number(value || 0) * 100);
// These are read-only overview selections, never budget-tree nodes or pot assignments.
const fundingGroups = [
  { label: 'All CRF', value: 'group:crf', category: 'CRF' },
  { label: 'All EI', value: 'group:ei', category: 'EI' },
  { label: 'All Salary', value: 'group:salary', category: 'salary' },
  { label: 'All Other Admin', value: 'group:other-admin', category: 'other-admin' },
];
// NWAC grouping confirmed by Bill on October 12, 2026. Yukon uses YT in PATH.
const regionalGroups = [
  { label: 'Eastern Region', value: 'region:eastern', codes: ['NS', 'NB', 'PE', 'NL', 'ON'] },
  { label: 'Southern Region', value: 'region:southern', codes: ['QC', 'MB', 'SK', 'NU'] },
  { label: 'Western Region', value: 'region:western', codes: ['AB', 'BC', 'YT', 'NT'] },
];
const regionCode = code => String(code).toUpperCase() === 'YK' ? 'YT' : String(code).toUpperCase();
function potCategory(pot) {
  if (String(pot.potType || '').toLowerCase().replace(/[_\s]+/g, ' ') === 'funding stream') {
    return String(pot.fundingSource || '').toUpperCase();
  }
  const code = pot.displayCode || pot.code || '';
  const name = pot.displayName || pot.name || '';
  if (/(?:^|[-_])SAL(?:[-_]|$)/i.test(code) || /\bsalar(?:y|ies)\b/i.test(name)) return 'salary';
  if (/(?:^|[-_])(?:OPS|TRV)(?:[-_]|$)/i.test(code) || /\b(?:other admin|operating|travel)\b/i.test(name)) return 'other-admin';
  return null;
}
function selectionPots(pots, selection) {
  const byId = new Map(pots.map(pot => [String(pot.id), pot]));
  if (selection === 'all') return pots.filter(pot => !byId.has(String(pot.parentId)));
  const group = [...fundingGroups, ...regionalGroups].find(item => item.value === selection);
  if (!group) return pots.filter(pot => String(pot.id) === String(selection));
  const matching = pots.filter(pot => {
    if (group.category) return potCategory(pot) === group.category;
    const visited = new Set();
    let current = pot;
    while (current && !visited.has(String(current.id))) {
      visited.add(String(current.id));
      const codes = (current.regionCodes || []).map(regionCode);
      // A mixed-region parent must not pull unrelated provinces into a total.
      if (codes.length) return codes.every(code => group.codes.includes(code));
      current = byId.get(String(current.parentId));
    }
    return false;
  });
  const selectedIds = new Set(matching.map(pot => String(pot.id)));
  // Parent values already include descendants: count a selected branch once.
  return matching.filter(pot => {
    const visited = new Set([String(pot.id)]);
    let parent = byId.get(String(pot.parentId));
    while (parent && !visited.has(String(parent.id))) {
      if (selectedIds.has(String(parent.id))) return false;
      visited.add(String(parent.id));
      parent = byId.get(String(parent.parentId));
    }
    return true;
  });
}
function budgetOverview(pots, fiscalYear, potId = 'all', { unassignedFunding = 0 } = {}) {
  const selected = selectionPots(pots, potId);
  const allocation = selected.reduce((sum, pot) => sum + cents(pot.allocation), 0);
  const committed = selected.reduce((sum, pot) => sum + cents(pot.committed), 0);
  const monthly = new Map();
  for (const pot of selected) for (const item of pot.monthlyCommitted || []) monthly.set(item.month, (monthly.get(item.month) || 0) + cents(item.amount));
  const start = Number(fiscalYear.slice(0, 4));
  const projection = potId === 'all' && Number(unassignedFunding) > 0
    ? { status: 'insufficient_data', confidence: null, explanation: 'Funding without a valid budget pot must be assigned before an all-budget projection is available.' }
    : selected.length === 1 ? selected[0].projection : aggregateBudgetForecasts(selected.map(pot => pot.projection), { allocation: allocation / 100, fiscalYear });
  return { hasPots: selected.length > 0, allocation: allocation / 100, committed: committed / 100,
    remaining: (allocation - committed) / 100, remainingPercent: allocation ? (allocation - committed) / allocation * 100 : null,
    projection,
    months: Array.from({ length: 12 }, (_, index) => {
      const month = index < 9 ? index + 4 : index - 8;
      const key = `${index < 9 ? start : start + 1}-${String(month).padStart(2, '0')}`;
      return { month: key, label: ['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar'][index], amount: (monthly.get(key) || 0) / 100 };
    }) };
}
function budgetPotOptions(pots) {
  const byId = new Map(pots.map(pot => [String(pot.id), pot]));
  const compare = new Intl.Collator('en-CA', { sensitivity: 'base', numeric: true }).compare;
  const children = new Map();
  for (const pot of pots) {
    const parent = byId.has(String(pot.parentId)) ? String(pot.parentId) : null;
    if (!children.has(parent)) children.set(parent, []);
    children.get(parent).push(pot);
  }
  for (const siblings of children.values()) siblings.sort((a,b) => Number(a.name.trim().toLowerCase()==='nwac national') - Number(b.name.trim().toLowerCase()==='nwac national') || compare(a.name,b.name));
  const options = [{ label: 'All NWAC Program', value: 'all' }, ...[...fundingGroups, ...regionalGroups].map(({ label, value }) => ({ label, value }))], visited = new Set();
  function add(parent, names) {
    for (const pot of children.get(parent) || []) {
      if (visited.has(String(pot.id))) continue;
      visited.add(String(pot.id));
      const path = [...names, pot.name];
      // The single program root is already represented by All NWAC Program.
      if (parent !== null || (children.get(null) || []).length > 1) options.push({ label: path.join(' / '), value: String(pot.id) });
      add(String(pot.id), parent === null ? [] : path);
    }
  }
  add(null, []);
  return options;
}
// Display transformations retain the recorded monthly data and never feed balances.
function budgetChartSeries(summary, { mode = 'monthly', showAverage = false, showProjection = false } = {}) {
  const cumulative = mode === 'cumulative';
  let running = 0;
  const recorded = summary.months.map(month => ({ x: month.label, y: cumulative ? (running += cents(month.amount)) / 100 : month.amount }));
  const series = [{ title: cumulative ? 'Cumulative committed' : 'Committed', type: cumulative ? 'line' : 'bar', data: recorded }];
  if (cumulative) series.push({ title: 'Allocation', type: 'threshold', y: summary.allocation });
  if (showAverage && !cumulative) series.push({ title: '3-month average', type: 'line', data: summary.months.slice(2).map((month, index) => ({
    x: month.label, y: Math.round(summary.months.slice(index, index + 3).reduce((sum, item) => sum + cents(item.amount), 0) / 3) / 100,
  })) });
  const projection = summary.projection;
  if (showProjection && projection?.status === 'available' && Array.isArray(projection.monthlyProjected) && /^\d{4}-\d{2}-\d{2}$/.test(projection.asOf || '')) {
    const expected = new Map(projection.monthlyProjected.map(month => [month.month, month.amount]));
    const currentMonth = projection.asOf.slice(0, 7);
    let projectedRunning = 0;
    const projected = summary.months.map(month => {
      // Pending costs may belong to earlier scheduled months. Include their
      // contribution in the forecast endpoint without redrawing recorded history.
      const amount = expected.get(month.month);
      if (!Number.isFinite(amount)) return null;
      projectedRunning += cents(amount);
      return { x: month.label, y: cumulative ? projectedRunning / 100 : amount, month: month.month };
    });
    const firstFuture = summary.months.findIndex(month => month.month >= currentMonth);
    if (firstFuture >= 0 && projected.every(Boolean)) {
      // One preceding recorded point joins the cumulative line to its forecast naturally.
      const start = cumulative ? Math.max(0, firstFuture - 1) : firstFuture;
      const data = projected.slice(start).map(({ x, y }) => ({ x, y }));
      if (cumulative && firstFuture > 0) data[0] = Object.assign({}, recorded[firstFuture - 1]);
      if (cumulative) series.push({ title: 'Projection', type: 'line', color: '#8b5cf6', data });
      else series.push({ title: 'Projected additional funding', type: 'bar', color: '#8b5cf6',
        // Preserve each recorded month; only future estimated funding is added.
        // Only the increment is stacked: blue already contains future commitments.
        data: projected.map((point, index) => ({ x: point.x,
          y: Math.max(0, cents(point.y) - cents(summary.months[index].amount)) / 100 })) });
    }
  }
  return series;
}
function budgetChartDomain(series) {
  const values = [], stacked = new Map();
  for (const item of series) {
    if (item.type === 'threshold') values.push(item.y);
    else for (const point of item.data) {
      values.push(point.y);
      if (item.type === 'bar') stacked.set(point.x, (stacked.get(point.x) || 0) + cents(point.y));
    }
  }
  values.push(...Array.from(stacked.values(), value => value / 100));
  return [Math.min(0, ...values), Math.max(1, ...values)];
}
function projectedYearEndLabel(remaining) {
  if (!Number.isFinite(remaining)) return 'Not enough data';
  const amount = cents(remaining);
  if (amount === 0) return 'On budget';
  const label = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Math.abs(amount) / 100);
  return `${label} ${amount > 0 ? 'underspend' : 'overspend'}`;
}
module.exports = { budgetOverview, budgetPotOptions, budgetChartSeries, budgetChartDomain, projectedYearEndLabel };
